import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorNote, Loading } from '../components/ui';
import './guide.css';

/**
 * Renders a knowledgebase article from web/public/kb/*.html inside the SPA
 * shell, so it picks up the app nav and auth instead of being a bare static
 * page. The .html files under public/kb stay the single source of truth for
 * content (and still work as plain pages, e.g. for a future unauthenticated
 * use) — this just fetches and re-renders their .wrap content.
 */
export function Guide() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const page = slug || 'index';

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);

    fetch(`/kb/${page}.html`)
      .then((response) => {
        if (!response.ok) throw new Error(`Guide "${page}" not found`);
        return response.text();
      })
      .then((text) => {
        if (cancelled) return;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const wrap = doc.querySelector('.wrap');
        if (!wrap) throw new Error(`Guide "${page}" is missing its .wrap content`);

        // Rewrite links between KB pages to SPA routes so navigation between
        // guides stays client-side instead of a full page reload.
        wrap.querySelectorAll('a[href^="/kb/"]').forEach((anchor) => {
          const href = anchor.getAttribute('href') || '';
          const match = href.match(/^\/kb\/([\w-]+)\.html$/);
          if (!match) return;
          anchor.setAttribute('href', match[1] === 'index' ? '/guides' : `/guides/${match[1]}`);
          anchor.setAttribute('data-spa-link', 'true');
        });

        setHtml(wrap.innerHTML);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a[data-spa-link="true"]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      event.preventDefault();
      navigate(href);
    };

    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [navigate, html]);

  if (error) return <ErrorNote message={error} />;
  if (html === null) return <Loading />;

  return <div ref={containerRef} className="kb-page" dangerouslySetInnerHTML={{ __html: html }} />;
}
